export class CustomSelect {
  private element: HTMLElement;
  private button: HTMLButtonElement;
  private dropdown: HTMLElement;
  private behaviour: 'select' | 'menu';

  constructor(element: HTMLElement) {
    this.element = element;
    this.button = element.querySelector('.custom-select__toggle') as HTMLButtonElement;
    this.dropdown = element.querySelector('.custom-select__dropdown') as HTMLElement;
    this.behaviour = (element.dataset.behaviour as 'select' | 'menu') || 'select';

    this.init();
  }

  private init() {
    this.button.addEventListener('click', this.toggleDropdown.bind(this));
    this.dropdown.addEventListener('keydown', this.handleKeydown.bind(this));
    this.dropdown.addEventListener('click', this.handleOptionClick.bind(this));

    document.addEventListener('click', this.handleOutsideClick.bind(this));
  }

  private toggleDropdown() {
    const isExpanded = this.button.getAttribute('aria-expanded') === 'true';
    CustomSelect.closeAllDropdowns();
    this.button.setAttribute('aria-expanded', (!isExpanded).toString());
    this.dropdown.hidden = isExpanded;
    if (!isExpanded) {
      this.dropdown.focus();
    }
  }

  private handleKeydown(e: KeyboardEvent) {
    const options = Array.from(this.dropdown.querySelectorAll('li')) as HTMLElement[];
    let index = options.indexOf(document.activeElement as HTMLElement);

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        options[(index + 1) % options.length].focus();
        break;
      case 'ArrowUp':
        e.preventDefault();
        options[(index - 1 + options.length) % options.length].focus();
        break;
      case 'Enter':
        if (this.behaviour === 'select') {
          this.selectOption(options[index]);
        } else {
          // menu mode, fire action but don't close
          options[index].click();
        }
        break;
      case 'Escape':
        e.preventDefault();
        this.closeDropdown();
        break;
    }
  }

  private handleOptionClick(e: Event) {
    const option = (e.target as HTMLElement).closest('li') as HTMLElement | null;

    if (!option) {
      return;
    }

    if (this.behaviour === 'menu') {
      // fire event and close, do not select
      this.element.dispatchEvent(new CustomEvent('custom-select:change', {
        detail: {
          value: option.dataset.value,
          name: option.dataset.name,
          element: option,
        },
      }));
      this.closeDropdown();
      return;
    }

    // select mode
    this.selectOption(option);
  }

  private selectOption(option: HTMLElement) {
    const prev = this.dropdown.querySelector('[aria-selected="true"]') as HTMLElement;
    if (prev) {
      prev.setAttribute('aria-selected', 'false');
    }
    option.setAttribute('aria-selected', 'true');

    this.button.dataset.disabled = option.dataset.disabled;
    this.button.innerHTML = option.innerHTML;

    this.closeDropdown();

    // ✅ Dispatch a custom event with the selected value
    this.element.dispatchEvent(new CustomEvent('custom-select:change', {
      detail: {
        value: option.dataset.value,
        name: option.dataset.name,
        element: option,
      },
    }));
  }

  private closeDropdown() {
    if (!this.dropdown.hidden) {
      this.dropdown.hidden = true;
      this.button.setAttribute('aria-expanded', 'false');
      this.button.focus();
    }
  }

  private handleOutsideClick(e: MouseEvent) {
    if (!this.element.contains(e.target as Node)) {
      this.dropdown.hidden = true;
      this.button.setAttribute('aria-expanded', 'false');
    }
  }

  private static closeAllDropdowns() {
    document.querySelectorAll('.js-custom-select').forEach((select) => {
      const button = select.querySelector('.custom-select__toggle') as HTMLButtonElement;
      const dropdown = select.querySelector('.custom-select__dropdown') as HTMLElement;
      if (dropdown && button && dropdown.hidden === false) {
        dropdown.hidden = true;
        button.setAttribute('aria-expanded', 'false');
      }
    });
  }
}
